// eventmanagement.jsx
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import JapaneseHolidays from 'japanese-holidays';
import "./eventmanagement.css";
import useNotificationStore from '../stores/notificationStore'; // 通知用

/* ========================================
 * 定数
 * ======================================== */
const API_URL = import.meta.env.VITE_API_URL;

// デフォルト出陳料
const DEFAULT_FEES = [
  { class_name: "ベビークラス", price: 5000, sort_order: 1 },
  { class_name: "パピークラス", price: 5000, sort_order: 2 },
  { class_name: "ジュニアクラス", price: 6000, sort_order: 3 },
  { class_name: "インターミディエートクラス", price: 6000, sort_order: 4 },
  { class_name: "オープンクラス", price: 6000, sort_order: 5 },
  { class_name: "チャンピオンクラス", price: 6000, sort_order: 6 },
  { class_name: "ベテランクラス", price: 6000, sort_order: 7 },
];

/* ========================================
 * ヘルパー関数
 * ======================================== */
const pad2 = (n) => String(n).padStart(2, '0');

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d)) return '';
  return `${d.getFullYear()}/${pad2(d.getMonth() + 1)}/${pad2(d.getDate())}`;
};

const formatInputDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d)) return '';
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

const formatDateJP = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())} 開催`;
};

const formatDateTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return `${d.getFullYear()}/${pad2(d.getMonth() + 1)}/${pad2(d.getDate())} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};

const formatGender = (g) => {
  if (g === 'M') return '牡';
  if (g === 'F') return '牝';
  return g || '';
};

const formatRegNo = (entry) => {
  if (entry.reg_type === 'JKC') {
    const left = [entry.reg_no1, entry.reg_no2].filter(Boolean).join('-');
    const right = entry.reg_no3 || '';

    if (left && right) return `${left}/${right}`;
    if (left) return left;
    if (right) return right;
    return '';
  }

  if (entry.reg_type === 'EXPEDITION') {
    return entry.reg_free || '';
  }

  return '';
};

/* ========================================
 * 日付ピッカーコンポーネント
 * ======================================== */
function DatePickerField({ value, onChange, placeholder, hasError }) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeStartDate, setActiveStartDate] = useState(value ? new Date(value) : new Date());
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const displayValue = value ? formatDate(value) : '';

  const handleDateChange = (date) => {
    const formatted = formatInputDate(date);
    onChange({ target: { name: '', value: formatted } });
    setIsOpen(false);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange({ target: { name: '', value: '' } });
  };

  const getTileClassName = ({ date, view }) => {
    if (view !== 'month') return null;
    const dayOfWeek = date.getDay();
    if (dayOfWeek === 0 || JapaneseHolidays.isHoliday(date)) return 'em-calSunday';
    if (dayOfWeek === 6) return 'em-calSaturday';
    return null;
  };

  return (
    <div className="em-datepicker" ref={containerRef}>
      <div className={`em-datepickerInput ${hasError ? 'is-error' : ''}`} onClick={() => setIsOpen(!isOpen)}>
        <span className={displayValue ? '' : 'em-datepickerPlaceholder'}>
          {displayValue || placeholder || '日付を選択'}
        </span>
        <div className="em-datepickerActions">
          {value && (
            <button type="button" className="em-datepickerClear" onClick={handleClear} title="クリア">
              ✕
            </button>
          )}
          <span className="em-datepickerIcon">📅</span>
        </div>
      </div>

      {isOpen && (
        <div className="em-datepickerPopup">
          <Calendar
            onChange={handleDateChange}
            value={value ? new Date(value) : null}
            activeStartDate={activeStartDate}
            onActiveStartDateChange={({ activeStartDate: asd }) => setActiveStartDate(asd)}
            view="month"
            minDetail="month"
            formatDay={(locale, date) => date.getDate()}
            formatShortWeekday={(locale, date) => ['日', '月', '火', '水', '木', '金', '土'][date.getDay()]}
            calendarType="gregory"
            tileClassName={getTileClassName}
          />
        </div>
      )}
    </div>
  );
}

/* ========================================
 * メインコンポーネント
 * ======================================== */
export default function EventManagement() {
  const queryClient = useQueryClient();

  // 画面モード: list / form / entries
  const [mode, setMode] = useState("list");
  const [editingId, setEditingId] = useState(null);
  const isEdit = editingId !== null;

  // エントリー一覧用
  const [entriesEventId, setEntriesEventId] = useState(null);
  const [entriesEventName, setEntriesEventName] = useState("");
  // エントリー検索・フィルター用
  const [entrySearchQuery, setEntrySearchQuery] = useState("");
  const [entryClassFilter, setEntryClassFilter] = useState("");
  const [entryGenderFilter, setEntryGenderFilter] = useState("");

  // エントリーページネーション用
  const [entryCurrentPage, setEntryCurrentPage] = useState(1);
  const [entryPageSize, setEntryPageSize] = useState(50);
  // 通知
  const { notification, showNotification } = useNotificationStore();

  // フォーム初期値
  const emptyForm = useMemo(() => ({
    event_name: "",
    summary: "",
    venue: "",
    organizer_name: "",
    person_in_charge: "",
    contact_tel: "",
    rental_destination: "",
    organizer_email1: "",
    organizer_email2: "",
    notes: "",
    judging: "",
    breed: "",
    event_date: "",
    apply_start_date: "",
    apply_end_date: "",
    max_entries: "",
    list_sending_time: "",
    printing_company_email: "",
    fees: DEFAULT_FEES.map(f => ({ ...f })),
  }), []);

  const [formData, setFormData] = useState(emptyForm);
  const [errors, setErrors] = useState({});

  /* ========================================
   * イベント一覧取得
   * ======================================== */
  const {
    data: eventsData,
    isLoading: isListLoading,
    isError: isListError,
    error: listError,
  } = useQuery({
    queryKey: ['event-management-list'],
    queryFn: async () => {
      const res = await axios.get(`${API_URL}/api/admin/events/list/`);
      if (!res.data?.success) throw new Error('イベント一覧の取得に失敗しました');
      return res.data.data || {};
    },
    retry: 1,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  });

  const events    = eventsData?.events    || [];
  const operators = eventsData?.operators || [];

  /* ========================================
   * イベント詳細取得
   * ======================================== */
  const {
    data: eventDetailData,
    isLoading: isDetailLoading,
    isError: isDetailError,
    error: detailError,
    refetch: refetchDetail,
  } = useQuery({
    queryKey: ['event-management-detail', editingId],
    queryFn: async () => {
      if (!editingId) return null;
      const res = await axios.get(`${API_URL}/api/admin/events/get/${editingId}/`);
      if (!res.data?.success) throw new Error('イベント詳細の取得に失敗しました');
      return res.data.data;
    },
    enabled: !!editingId && mode === 'form',
    retry: 1,
    staleTime: 0,
  });

  // 詳細データをフォームに反映
  useEffect(() => {
    if (!eventDetailData) return;

    setFormData({
      event_name: eventDetailData.event_name || "",
      summary: eventDetailData.summary || "",
      venue: eventDetailData.venue || "",
      organizer_name: eventDetailData.organizer_name || "",
      person_in_charge: eventDetailData.person_in_charge || "",
      contact_tel: eventDetailData.contact_tel || "",
      rental_destination: eventDetailData.rental_destination || "",
      organizer_email1: eventDetailData.organizer_email1 || "",
      organizer_email2: eventDetailData.organizer_email2 || "",
      notes: eventDetailData.notes || "",
      judging: eventDetailData.judging || "",
      breed: eventDetailData.breed || "",
      event_date: eventDetailData.event_date || "",
      apply_start_date: eventDetailData.apply_start_date || "",
      apply_end_date: eventDetailData.apply_end_date || "",
      max_entries: eventDetailData.max_entries || "",
      list_sending_time: eventDetailData.list_sending_time || "",
      printing_company_email: eventDetailData.printing_company_email || "",
      fees: eventDetailData.fees && eventDetailData.fees.length > 0
        ? eventDetailData.fees.map(f => ({ ...f }))
        : DEFAULT_FEES.map(f => ({ ...f })),
    });
    setErrors({});
  }, [eventDetailData]);

  /* ========================================
   * エントリー一覧取得
   * ======================================== */
  const {
    data: entriesData,
    isLoading: isEntriesLoading,
    isError: isEntriesError,
    error: entriesError,
  } = useQuery({
    queryKey: ['event-entries', entriesEventId],
    queryFn: async () => {
      if (!entriesEventId) return null;
      const res = await axios.get(`${API_URL}/api/admin/events/${entriesEventId}/entries/`);
      if (!res.data?.success) throw new Error('エントリー一覧の取得に失敗しました');
      return res.data.data;
    },
    enabled: !!entriesEventId && mode === 'entries',
    retry: 1,
    staleTime: 0,
  });

  const entries = entriesData?.entries || [];

  const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

  // エントリーのフィルタリング
  const filteredEntries = useMemo(() => {
    const q = entrySearchQuery.trim().toLowerCase();
    return entries.filter((entry) => {
      const matchesQuery =
        !q ||
        (entry.pedigree_name || '').toLowerCase().includes(q) ||
        (entry.exhibitor_name || '').toLowerCase().includes(q) ||
        (entry.handler_name || '').toLowerCase().includes(q) ||
        (entry.breed_group || '').toLowerCase().includes(q) ||
        (formatRegNo(entry) || '').toLowerCase().includes(q);

      const matchesClass = !entryClassFilter || entry.class_name === entryClassFilter;
      const matchesGender = !entryGenderFilter || entry.gender === entryGenderFilter;

      return matchesQuery && matchesClass && matchesGender;
    });
  }, [entries, entrySearchQuery, entryClassFilter, entryGenderFilter]);

  // クラス名の選択肢を動的に生成
  const entryClassOptions = useMemo(() => {
    const classes = [...new Set(entries.map((e) => e.class_name).filter(Boolean))];
    return classes.sort();
  }, [entries]);

  // ページネーション計算
  const entryTotalItems = filteredEntries.length;
  const entryTotalPages = Math.ceil(entryTotalItems / entryPageSize);
  const entryStartIndex = (entryCurrentPage - 1) * entryPageSize;
  const entryEndIndex = Math.min(entryStartIndex + entryPageSize, entryTotalItems);
  const paginatedEntries = filteredEntries.slice(entryStartIndex, entryEndIndex);

  // フィルター変更時にページリセット
  useEffect(() => {
    setEntryCurrentPage(1);
  }, [entrySearchQuery, entryClassFilter, entryGenderFilter]);

  const handleEntryResetFilters = () => {
    setEntrySearchQuery('');
    setEntryClassFilter('');
    setEntryGenderFilter('');
    setEntryCurrentPage(1);
  };

  const handleEntryPageSizeChange = (newSize) => {
    setEntryPageSize(newSize);
    setEntryCurrentPage(1);
  };

  /* ========================================
   * 作成 Mutation
   * ======================================== */
  const createMutation = useMutation({
    mutationFn: async (payload) => {
      const res = await axios.post(`${API_URL}/api/admin/events/create/`, payload);
      if (!res.data?.success) throw new Error(res.data?.message || '作成に失敗しました');
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['event-management-list'] });
      showNotification('イベントを作成しました');
      setMode("list");
      setEditingId(null);
    },
    onError: (err) => {
      const message = err.response?.data?.message || err.message || '作成に失敗しました';
      window.alert(message);
    },
  });

  /* ========================================
   * 更新 Mutation
   * ======================================== */
  const updateMutation = useMutation({
    mutationFn: async ({ eventId, payload }) => {
      const res = await axios.put(`${API_URL}/api/admin/events/update/${eventId}/`, payload);
      if (!res.data?.success) throw new Error(res.data?.message || '更新に失敗しました');
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['event-management-list'] });
      queryClient.invalidateQueries({ queryKey: ['event-management-detail', editingId] });
      showNotification('イベントを更新しました');
      setMode("list");
      setEditingId(null);
    },
    onError: (err) => {
      const message = err.response?.data?.message || err.message || '更新に失敗しました';
      window.alert(message);
    },
  });

  /* ========================================
   * 削除 Mutation
   * ======================================== */
  const deleteMutation = useMutation({
    mutationFn: async (eventId) => {
      const res = await axios.delete(`${API_URL}/api/admin/events/delete/${eventId}/`);
      if (!res.data?.success) throw new Error(res.data?.message || '削除に失敗しました');
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['event-management-list'] });
      showNotification('イベントを削除しました');
    },
    onError: (err) => {
      const message = err.response?.data?.message || err.message || '削除に失敗しました';
      window.alert(message);
    },
  });

  const isSaving = createMutation.isPending || updateMutation.isPending;
  const isDeleting = deleteMutation.isPending;

  /* ========================================
   * 画面遷移
   * ======================================== */
  const openCreate = () => {
    setEditingId(null);
    setErrors({});
    setFormData(emptyForm);
    setMode("form");
  };

  const openEdit = (ev) => {
    setEditingId(ev.event_id);
    setErrors({});
    setMode("form");
  };

  // エントリー一覧を開く
  const openEntries = (ev) => {
    setEntriesEventId(ev.event_id);
    setEntriesEventName(ev.event_name);
    setEntrySearchQuery('');
    setEntryClassFilter('');
    setEntryGenderFilter('');
    setEntryCurrentPage(1);
    setMode("entries");
  };

  const handleBack = () => {
    setErrors({});
    setEditingId(null);
    setEntriesEventId(null);
    setEntriesEventName("");
    setMode("list");
  };

  /* ========================================
   * フォーム操作
   * ======================================== */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleDateChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleFeeChange = (idx, value) => {
    setFormData((prev) => {
      const next = [...prev.fees];
      next[idx] = {
        ...next[idx],
        price: value === '' ? '' : parseInt(value, 10),
      };
      return { ...prev, fees: next };
    });
  };
  /* ========================================
   * バリデーション
   * ======================================== */
  const validate = () => {
    const newErrors = {};
    if (!formData.event_name.trim()) newErrors.event_name = "イベント名は必須です";
    if (!formData.venue.trim()) newErrors.venue = "開催場所は必須です";
    if (!formData.organizer_name.trim()) newErrors.organizer_name = "主催者名は必須です";
    if (!formData.contact_tel.trim()) newErrors.contact_tel = "連絡先電話番号は必須です";
    if (!formData.organizer_email1.trim()) newErrors.organizer_email1 = "主催者メールアドレス1は必須です";
    if (!formData.event_date) newErrors.event_date = "開催日は必須です";
    if (!formData.apply_start_date) newErrors.apply_start_date = "申込開始日は必須です";
    if (!formData.apply_end_date) newErrors.apply_end_date = "申込終了日は必須です";
    if (
      formData.printing_company_email.trim() &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.printing_company_email.trim())
    ) {
      newErrors.printing_company_email = "有効なメールアドレスを入力してください";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /* ========================================
   * 保存
   * ======================================== */
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    const payload = {
      event_name: formData.event_name,
      summary: formData.summary || null,
      venue: formData.venue,
      organizer_name: formData.organizer_name,
      person_in_charge: formData.person_in_charge || null,
      contact_tel: formData.contact_tel,
      rental_destination: formData.rental_destination || null,
      organizer_email1: formData.organizer_email1,
      organizer_email2: formData.organizer_email2 || null,
      notes: formData.notes || null,
      judging: formData.judging || null,
      breed: formData.breed || null,
      event_date: formData.event_date,
      apply_start_date: formData.apply_start_date,
      apply_end_date: formData.apply_end_date,
      max_entries: formData.max_entries ? parseInt(formData.max_entries, 10) : null,
      list_sending_time: formData.list_sending_time || null,
      printing_company_email: formData.printing_company_email.trim() || null,
      fees: formData.fees.map((f, idx) => ({
        event_fee_id: f.event_fee_id,
        class_name: f.class_name,
        price: f.price === '' ? 0 : Number(f.price),
        sort_order: f.sort_order || idx + 1,
      })),
    };

    if (isEdit) {
      updateMutation.mutate({ eventId: editingId, payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  /* ========================================
   * 削除
   * ======================================== */
  const handleExportExcel = async (eventId) => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/events/${eventId}/entries/excel/`, {
        responseType: 'blob',
      });
      const disposition = res.headers['content-disposition'] || '';
      const match = disposition.match(/filename\*=UTF-8''([^;]+)/);
      const filename = match ? decodeURIComponent(match[1]) : `entries_${ev.event_id}.xlsx`;

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      window.alert('Excelの出力に失敗しました');
    }
  };

  const handleDelete = (ev) => {
    // エントリーがある場合は削除不可（念のためチェック）
    if (ev.entry_count > 0) {
      window.alert(`このイベントにはすでに ${ev.entry_count} 件のエントリーがあるため削除できません`);
      return;
    }

    if (!window.confirm(`「${ev.event_name}」を削除しますか？\nこの操作は取り消せません。`)) {
      return;
    }
    deleteMutation.mutate(ev.event_id);
  };

  /* ========================================
   * エントリー一覧画面
   * ======================================== */
  if (mode === "entries") {
    return (
      <div className="em-page em-page--entries">
        <section className="em-section em-section--entries">
          <div className="em-formHeader em-entriesHeaderRow">
            <div className="em-entriesTitle">
              {entriesEventName}　─　エントリー一覧
            </div>
            <button
              type="button"
              className="btn btn-success"
              onClick={() => handleExportExcel(entriesEventId)}
            >
              ⬇ Excel出力
            </button>
          </div>

          <div className="em-filterBar">
            <input
              className="em-searchInput"
              placeholder="犬名・出陳者名・取扱者名で検索"
              value={entrySearchQuery}
              onChange={(e) => setEntrySearchQuery(e.target.value)}
            />

            <div className="em-selectWrapper">
              <select
                className="em-filterSelect"
                value={entryClassFilter}
                onChange={(e) => setEntryClassFilter(e.target.value)}
              >
                <option value="">すべてのクラス</option>
                {entryClassOptions.map((cls) => (
                  <option key={cls} value={cls}>{cls}</option>
                ))}
              </select>
            </div>

            <div className="em-selectWrapper">
              <select
                className="em-filterSelect"
                value={entryGenderFilter}
                onChange={(e) => setEntryGenderFilter(e.target.value)}
              >
                <option value="">すべての性別</option>
                <option value="M">牡</option>
                <option value="F">牝</option>
              </select>
            </div>

            <button
              type="button"
              className="em-ghostButton"
              onClick={handleEntryResetFilters}
            >
              条件リセット
            </button>

            {/* ページネーション */}
            <div className="em-paginationGroup">
              <span className="em-paginationLabel">表示件数:</span>
              <div className="em-selectWrapper em-selectWrapper--small">
                <select
                  className="em-filterSelect"
                  value={entryPageSize}
                  onChange={(e) => handleEntryPageSizeChange(Number(e.target.value))}
                >
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <option key={size} value={size}>{size}</option>
                  ))}
                </select>
              </div>
              <span className="em-paginationInfo">
                {entryTotalItems > 0
                  ? `${entryStartIndex + 1}-${entryEndIndex} / ${entryTotalItems}`
                  : '0 件'}
              </span>
              <div className="em-paginationButtons">
                <button
                  className="em-paginationBtn"
                  onClick={() => setEntryCurrentPage(1)}
                  disabled={entryCurrentPage === 1}
                >|&lt;</button>
                <button
                  className="em-paginationBtn"
                  onClick={() => setEntryCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={entryCurrentPage === 1}
                >&lt;</button>
                <button
                  className="em-paginationBtn"
                  onClick={() => setEntryCurrentPage((p) => Math.min(entryTotalPages, p + 1))}
                  disabled={entryCurrentPage >= entryTotalPages}
                >&gt;</button>
                <button
                  className="em-paginationBtn"
                  onClick={() => setEntryCurrentPage(entryTotalPages)}
                  disabled={entryCurrentPage >= entryTotalPages}
                >&gt;|</button>
              </div>
            </div>
          </div>

          {isEntriesError && (
            <div className="em-error">
              エラー: {entriesError?.message || 'エントリー一覧の取得に失敗しました'}
            </div>
          )}

          {isEntriesLoading ? (
            <div className="em-loading">読み込み中...</div>
          ) : entries.length === 0 ? (
            <div className="em-empty">エントリーはありません。</div>
          ) : (
            <>
              <div className="em-count">
                {filteredEntries.length !== entries.length
                  ? `${filteredEntries.length} 件 / 全 ${entries.length} 件`
                  : `全 ${entries.length} 件`}
              </div>

              <div className="em-tableWrapper">
                <table className="em-table">
                  <thead>
                    <tr>
                      <th>受付No</th>
                      <th>登録番号</th>
                      <th>生年月日</th>
                      <th>性別</th>
                      <th>犬種</th>
                      <th>犬種名/バラエティー</th>
                      <th>出陳クラス</th>
                      <th>ユーザー名</th>
                      <th>メールアドレス</th>
                      <th>電話番号</th>
                      <th>犬名</th>
                      <th>父犬名</th>
                      <th>母犬名</th>
                      <th>出陳者名</th>
                      <th>出陳者都道府県</th>
                      <th>出陳取扱者名</th>
                      <th>出陳取扱者都道府県</th>
                      <th>備考</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedEntries.map((entry, idx) => (
                      <tr key={entry.entry_id}>
                        <td>{entryStartIndex + idx + 1}</td>
                        <td>{formatRegNo(entry)}</td>
                        <td>{formatDate(entry.birth_date)}</td>
                        <td>{formatGender(entry.gender)}</td>
                        <td>{entry.breed_group}</td>
                        <td>{entry.breed_option}</td>
                        <td>{entry.class_name}</td>
                        <td>{entry.user_name || '—'}</td>
                        <td>{entry.user_email || '—'}</td>
                        <td>{entry.phone || '—'}</td>
                        <td>{entry.pedigree_name}</td>
                        <td>{entry.sire_name || '—'}</td>
                        <td>{entry.dam_name || '—'}</td>
                        <td>{entry.exhibitor_name || '—'}</td>
                        <td>{entry.exhibitor_pref || '—'}</td>
                        <td>{entry.handler_name || '—'}</td>
                        <td>{entry.handler_pref || '—'}</td>
                        <td>{entry.notes || ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          <div className="em-footer em-footer--entries">
            <button type="button" className="btn btn-secondary" onClick={handleBack}>
              戻る
            </button>
          </div>
        </section>
      </div>
    );
  }

  /* ========================================
   * フォーム画面
   * ======================================== */
  if (mode === "form") {
    const isFormLoading = isEdit && isDetailLoading;
    const isFormError = isEdit && isDetailError;

    return (
      <div className="em-page">
        
        <section className="em-section">
          <div className="em-formHeader">
            <h3 className="em-formTitle">
              {isEdit ? 'イベント編集' : 'イベント新規作成'}　※は必須です
            </h3>
          </div>

          {isFormError && (
            <div className="em-error">
              エラー: {detailError?.message || 'イベント詳細の取得に失敗しました'}
            </div>
          )}

          {isFormLoading ? (
            <div className="em-loading">読み込み中...</div>
          ) : (
            <form className="em-form" onSubmit={handleSubmit}>
              {/* イベント名 */}
              <div className="em-formGroup em-formGroup--full">
                <label className="em-label">
                  イベント名 <span className="em-required">※</span>
                </label>
                <input
                  name="event_name"
                  className={`em-input ${errors.event_name ? 'is-error' : ''}`}
                  value={formData.event_name}
                  onChange={handleChange}
                  placeholder="イベント名"
                />
                {errors.event_name && <span className="em-errorText">{errors.event_name}</span>}
              </div>

              {/* 概要 / 開催場所 */}
              <div className="em-formGrid">
                <div className="em-formGroup">
                  <label className="em-label">概要</label>
                  <input
                    name="summary"
                    className="em-input"
                    value={formData.summary}
                    onChange={handleChange}
                    placeholder="概要"
                  />
                </div>

                <div className="em-formGroup">
                  <label className="em-label">
                    開催場所 <span className="em-required">※</span>
                  </label>
                  <input
                    name="venue"
                    className={`em-input ${errors.venue ? 'is-error' : ''}`}
                    value={formData.venue}
                    onChange={handleChange}
                    placeholder="開催場所"
                  />
                  {errors.venue && <span className="em-errorText">{errors.venue}</span>}
                </div>
              </div>

              {/* 主催者 / 事務所 / 連絡先 */}
              <div className="em-formGrid">
                <div className="em-formGroup">
                  <label className="em-label">
                    主催者 <span className="em-required">※</span>
                  </label>
                  <input
                    name="organizer_name"
                    className={`em-input ${errors.organizer_name ? 'is-error' : ''}`}
                    value={formData.organizer_name}
                    onChange={handleChange}
                    placeholder="主催者"
                  />
                  {errors.organizer_name && <span className="em-errorText">{errors.organizer_name}</span>}
                </div>

                <div className="em-formGroup">
                  <label className="em-label">事務所</label>
                  <input
                    name="person_in_charge"
                    className="em-input"
                    value={formData.person_in_charge}
                    onChange={handleChange}
                    placeholder="事務所"
                  />
                </div>

                <div className="em-formGroup">
                  <label className="em-label">
                    連絡先電話番号 <span className="em-required">※</span>
                  </label>
                  <input
                    name="contact_tel"
                    className={`em-input ${errors.contact_tel ? 'is-error' : ''}`}
                    value={formData.contact_tel}
                    onChange={handleChange}
                    placeholder="連絡先電話番号"
                  />
                  {errors.contact_tel && <span className="em-errorText">{errors.contact_tel}</span>}
                </div>

                <div className="em-formGroup">
                  <label className="em-label">貸出先</label>
                  <select
                    name="rental_destination"
                    className="em-input"
                    value={formData.rental_destination}
                    onChange={handleChange}
                  >
                    <option value="">― 選択してください ―</option>
                    {operators.map((op) => (
                      <option key={op.user_id} value={op.user_id}>
                        {op.user_name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* メール */}
              <div className="em-formGrid">
                <div className="em-formGroup">
                  <label className="em-label">
                    主催者メールアドレス1 <span className="em-required">※</span>
                  </label>
                  <input
                    name="organizer_email1"
                    type="email"
                    className={`em-input ${errors.organizer_email1 ? 'is-error' : ''}`}
                    value={formData.organizer_email1}
                    onChange={handleChange}
                    placeholder="メールアドレス1"
                  />
                  {errors.organizer_email1 && <span className="em-errorText">{errors.organizer_email1}</span>}
                </div>

                <div className="em-formGroup">
                  <label className="em-label">主催者メールアドレス2</label>
                  <input
                    name="organizer_email2"
                    type="email"
                    className="em-input"
                    value={formData.organizer_email2}
                    onChange={handleChange}
                    placeholder="メールアドレス2"
                  />
                </div>
              </div>

              {/* テキストエリア */}
              <div className="em-formGroup em-formGroup--full">
                <label className="em-label">注意事項</label>
                <textarea
                  name="notes"
                  className="em-textarea em-textarea--lg"
                  value={formData.notes}
                  onChange={handleChange}
                  placeholder="注意事項"
                />
              </div>

              <div className="em-formGroup em-formGroup--full">
                <label className="em-label">審査委員長</label>
                <textarea
                  name="judging"
                  className="em-textarea em-textarea--sm"
                  value={formData.judging}
                  onChange={handleChange}
                  placeholder="審査委員長"
                />
              </div>

              <div className="em-formGroup em-formGroup--full">
                <label className="em-label">ブリード</label>
                <textarea
                  name="breed"
                  className="em-textarea em-textarea--sm"
                  value={formData.breed}
                  onChange={handleChange}
                  placeholder="ブリード"
                />
              </div>

              {/* 開催日と申込期間 */}
              <div className="em-sectionLabel">開催日と申込期間</div>
              <div className="em-datesRow">
                <div className="em-formGroup">
                  <label className="em-label">
                    開催日 <span className="em-required">※</span>
                  </label>
                  <DatePickerField
                    value={formData.event_date}
                    onChange={(e) => handleDateChange('event_date', e.target.value)}
                    placeholder="年/月/日"
                    hasError={!!errors.event_date}
                  />
                  {errors.event_date && <span className="em-errorText">{errors.event_date}</span>}
                </div>

                <div className="em-formGroup">
                  <label className="em-label">
                    申込開始日 <span className="em-required">※</span>
                  </label>
                  <DatePickerField
                    value={formData.apply_start_date}
                    onChange={(e) => handleDateChange('apply_start_date', e.target.value)}
                    placeholder="年/月/日"
                    hasError={!!errors.apply_start_date}
                  />
                  {errors.apply_start_date && <span className="em-errorText">{errors.apply_start_date}</span>}
                </div>

                <div className="em-formGroup">
                  <label className="em-label">
                    申込終了日 <span className="em-required">※</span>
                  </label>
                  <DatePickerField
                    value={formData.apply_end_date}
                    onChange={(e) => handleDateChange('apply_end_date', e.target.value)}
                    placeholder="年/月/日"
                    hasError={!!errors.apply_end_date}
                  />
                  {errors.apply_end_date && <span className="em-errorText">{errors.apply_end_date}</span>}
                </div>

                <div className="em-formGroup">
                  <label className="em-label">参加上限数</label>
                  <input
                    type="number"
                    name="max_entries"
                    className="em-input"
                    value={formData.max_entries}
                    onChange={handleChange}
                    min="0"
                  />
                </div>
              </div>

              {/* 一覧送付設定 */}
              <div className="em-sectionLabel">一覧Excel送付設定</div>
              <div className="em-formGrid">
                <div className="em-formGroup">
                  <label className="em-label">一覧Excel送付時間</label>
                  <input
                    type="time"
                    name="list_sending_time"
                    className="em-input"
                    value={formData.list_sending_time}
                    onChange={handleChange}
                  />
                </div>

                <div className="em-formGroup">
                  <label className="em-label">一覧Excel送信先メールアドレス</label>
                  <input
                    type="email"
                    name="printing_company_email"
                    maxLength={320}
                    className={`em-input ${errors.printing_company_email ? 'is-error' : ''}`}
                    value={formData.printing_company_email}
                    onChange={handleChange}
                    placeholder="一覧Excel送信先メールアドレス"
                  />
                  {errors.printing_company_email && (
                    <span className="em-errorText">{errors.printing_company_email}</span>
                  )}
                </div>
              </div>

              {/* 出陳料 */}
              <div className="em-sectionLabel">出陳料</div>
              <div className="em-feesTable">
                <div className="em-feesHead">
                  <div className="em-feesColClass">区分</div>
                  <div className="em-feesColPrice">価格</div>
                </div>

                {formData.fees.map((fee, idx) => (
                  <div key={idx} className="em-feesRow">
                    <div className="em-feesColClass">{fee.class_name}</div>
                    <div className="em-feesColPrice">
                      <input
                        type="number"
                        className="em-input"
                        value={fee.price}
                        onChange={(e) => handleFeeChange(idx, e.target.value)}
                        min="0"
                      />
                    </div>
                  </div>
                ))}
              </div>

              {/* Footer */}
              <div className="em-footer">
                <button type="button" className="btn btn-secondary" onClick={handleBack} disabled={isSaving}>
                  戻る
                </button>
                <button type="submit" className="btn btn-primary" disabled={isSaving}>
                  {isSaving ? '保存中...' : isEdit ? '更新する' : '作成する'}
                </button>
              </div>
            </form>
          )}
        </section>
      </div>
    );
  }

  /* ========================================
   * 一覧画面
   * ======================================== */
  return (
    <div className="em-page">
      {notification && (
        <div className={`notification notification-${notification.type}`}>
          {notification.message}
        </div>
      )}

      <div className="em-card">
        <div className="em-cardHeader">
          <div className="em-cardTitle">イベント管理</div>

          <button type="button" className="btn btn-primary" onClick={openCreate}>
            ＋イベント追加
          </button>
        </div>

        {isListError && (
          <div className="em-error">
            エラー: {listError?.message || 'イベント一覧の取得に失敗しました'}
          </div>
        )}

        {isListLoading ? (
          <div className="em-loading">読み込み中...</div>
        ) : events.length === 0 ? (
          <div className="em-empty">
            イベントがありません。「＋イベント追加」ボタンから新規作成してください。
          </div>
        ) : (
          <div className="em-list">
            {events.map((ev) => (
              <div key={ev.event_id} className="em-listRow">
                <div className="em-listRowLeft">
                  <div className="em-listName">
                    {ev.event_name}{" "}
                    {ev.summary && (
                      <span className="em-listMeta">
                       【{ev.summary}】
                      </span>
                    )}
                  </div>
                  <div className="em-listDate">{formatDateJP(ev.event_date)}</div>
                  <div className="em-listVenue">{ev.venue}</div>
                </div>

                <div className="em-listRowRight">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => openEdit(ev)}
                    disabled={isDeleting}
                  >
                    ✎ 編集
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => openEntries(ev)}
                    disabled={isDeleting}
                  >
                    ⧉ エントリー一覧
                  </button>
                  {ev.entry_count > 0 ? (
                    <span
                      className="em-tooltipWrapper"
                      data-tooltip={`エントリーが ${ev.entry_count} 件あるため削除できません`}
                    >
                      <button
                        type="button"
                        className="btn btn-danger"
                        disabled
                      >
                        🗑 削除
                      </button>
                    </span>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-danger"
                      onClick={() => handleDelete(ev)}
                      disabled={isDeleting}
                    >
                      🗑 削除
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}